import { cn } from "@/lib/utils";

const FULL_RATIO = 1;
const EMPTY_RATIO = 0;
const PERCENT_MULTIPLIER = 100;

// With no included allowance there is no ratio to show: any usage at all
// reads as a full bar, none as empty.
function getUsageMeterRatio(used: number, includedQuantity: number): number {
  if (includedQuantity > 0) {
    return Math.min(used / includedQuantity, FULL_RATIO);
  }
  if (used > EMPTY_RATIO) {
    return FULL_RATIO;
  }
  return EMPTY_RATIO;
}

type UsageBillingUsageMeterProps = {
  usedQuantity: string;
  includedQuantity: number;
  isOverage: boolean;
};

// A meter, not a chart: one ratio (used vs. included) against a limit. The
// numeric "used (included)" text next to it is the source of truth; this bar
// is a restrained at-a-glance scan aid, so color alone never carries meaning.
export function UsageBillingUsageMeter({
  usedQuantity,
  includedQuantity,
  isOverage,
}: UsageBillingUsageMeterProps) {
  const used = Number(usedQuantity);
  const ratio = getUsageMeterRatio(used, includedQuantity);

  return (
    <div
      role="img"
      aria-label={`${used} of ${includedQuantity} used${isOverage ? ", over included amount" : ""}`}
      title={`${used} / ${includedQuantity} used`}
      className="ml-auto h-1 w-12 overflow-hidden rounded-full bg-gray-200"
    >
      <div
        className={cn(
          "h-full rounded-full",
          isOverage ? "bg-amber-500" : "bg-gray-400",
        )}
        style={{ width: `${ratio * PERCENT_MULTIPLIER}%` }}
      />
    </div>
  );
}
